import React, { useEffect, useState } from "react";
import { getVisitsDetails, updateVisitRemark } from "_controllers/officer";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import haversine from "haversine-distance";
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import FlagIcon from '@mui/icons-material/Flag';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box } from "@mui/system";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import exportExcel from "utils/excelGenerate";
import RoomIcon from '@mui/icons-material/Room';
import './VisitList.css'
import { toastMessage } from "utils/toastMessage";
import { useSelector } from "react-redux";
import { PageHeader, SectionCard, EmptyState } from "_shared/ui";

const VisitsList = () => {
  const { SAP_ID } = useParams();
  const { user } = useSelector((state) => state.user);

  const [Values, setValues] = useState({
    selectStartDate: '',
    selectEndDate: '',
  });

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [message, setMessage] = useState("");
  const [visitDetails, setVisitDetails] = useState([])
  const [csvData, setCsvData] = useState([]);
  const [lat, setLat] = useState('')
  const [long, setLong] = useState('')
  const [openLoc, setOpenLoc] = useState(false);
  const [addRemark, setAddRemark] = useState(false)
  const [visitId, setVisitId] = useState('')
  const [visitRemark, setVisitRemark] = useState('')

  // Shared modal container styling — square, theme-aware surface.
  const modalCardSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    textAlign: 'left',
    letterSpacing: 1,
  };

  const messageStyleLoc = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 2,
    textAlign: 'center',
  };

  const fileName = `${SAP_ID}-visits`;

  useEffect(async () => {
    const start = startDate
      ? startDate
      : moment().startOf("month").format("YYYY-MM-DD");

    const end = endDate
      ? endDate
      : moment().endOf("month").format("YYYY-MM-DD");
    const plan = await getVisitsDetails({
      SAP_ID,
      start,
      end,
    });
    setData(plan?.data);
    setCsvData(plan?.data)
    setLoading(true);
    setMessage('')
  }, [message]);

  const handleClose = () => {
    setOpen(false);
    setOpenLoc(false)
  };

  const handleChange = (e) => {
    setValues({ ...Values, [e.target.name]: e.target.value })
  }

  const { selectStartDate, selectEndDate } = Values

  const handleSubmit = async (e) => {
    e.preventDefault()
    const plan = await getVisitsDetails({
      SAP_ID,
      start: selectStartDate,
      end: selectEndDate,
    });
    setData(plan?.data);
    setCsvData(plan?.data)
    setLoading(true);
  }

  const calculateDistance = (coordinates) => {
    const { lat1, long1, lat2, long2 } = coordinates
    const distance = haversine({ lat: lat1, lng: long1 }, { lat: lat2, lng: long2 })
    return isNaN(distance) ? "NA" : distance.toFixed(0)
  }

  const generateExcel = async (e) => {
    e.preventDefault()
    const visitact = []

    exportExcel(csvData.map((x) => {
      let temp = {}
      x?.visitRemark && x?.visitRemark.length > 0 ? x?.visitRemark.map((item, idx) => {
        item.role === "ZO" ?
          temp[`Visit_Remark_${idx + 1}`] = `ZM -${item.remark}` :
          (item.role === "ADMIN" ?
            temp[`Visit_Remark_${idx + 1}`] = `Sr VP Sales -${item.remark}` :
            (
              item.role === "RO" ?
                temp[`Visit_Remark_${idx + 1}`] = `RM -${item.remark}` :
                temp[`Visit_Remark_${idx + 1}`] = `AM -${item.remark}`
            )
          )

      }) : null

      let coordinates = {
        lat1: x?.startVisitLocation?.lat
          ? x?.startVisitLocation?.lat
          : 0,
        long1: x?.startVisitLocation?.long
          ? x?.startVisitLocation?.long
          : 0,
        lat2: x?.endVisitLocation?.lat
          ? x?.endVisitLocation?.lat
          : 0,
        long2: x?.endVisitLocation?.long
          ? x?.endVisitLocation?.long
          : 0
      }

      // console.log("temo", tempObj);

      return {
        'SAP ID': x?.SAP_ID,
        "Dealer Code": x.dealerCode,
        'Start Time': moment.utc(x.startTime).format("HH:mm"),
        'End Time': moment.utc(x.endTime).format("HH:mm"),
        "Start Date": moment.utc(x?.startTime).format("DD-MM-YYYY"),
        "End Date": x?.endTime ? moment.utc(x?.endTime).format("DD-MM-YYYY") : "NA",
        'Completed': x?.completed,
        "Remarks": x?.remark ? x.remark : "NA",
        "Start Location": `Lat : ${x.startVisitLocation.lat}, Long : ${x.startVisitLocation.long}`,
        "End Location": `Lat : ${x.endVisitLocation.lat}, Long : ${x.endVisitLocation.long}`,
        "Time Spent (mins)": moment.duration((moment(x.endTime ? x.endTime : "")).diff((moment(x.startTime ? x.startTime : "")))).asMinutes().toFixed(0),
        "Visit Type": x?.unplanned ? "Unplanned" : "Planned",
        "Distance Flag": calculateDistance(coordinates) < 100 ? "< 100 m" : ">100 m",
        ...temp
      }
    }), fileName)
  }

  const submitRemark = async () => {
    if (visitRemark === "")
      return alert("Remark Field Is Empty.");
    const data = await updateVisitRemark({
      id: visitId,
      remark: visitRemark
    });
    setAddRemark(false)
    setVisitRemark("")
    setVisitId('')
    setMessage(data?.message);
    toastMessage("Remark Added Successfully", "success")
  };

  return (
    <>
      <PageHeader
        title="Visits"
        subtitle={`Field visit log for SAP ID ${SAP_ID}`}
      />

      <SectionCard sx={{ mb: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                type={'date'}
                fullWidth
                size="small"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
                value={selectStartDate}
                name="selectStartDate"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                type={'date'}
                fullWidth
                size="small"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
                value={selectEndDate}
                name="selectEndDate"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1.5}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={handleSubmit}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={generateExcel}
                >
                  Download
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </SectionCard>

      {loading ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">Dealer Code</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Start Location</TableCell>
                <TableCell align="center">End Location</TableCell>
                <TableCell align="center">Start Time</TableCell>
                <TableCell align="center">End Time</TableCell>
                <TableCell align="center">Visit Action</TableCell>
                <TableCell align="center">Flag</TableCell>
                <TableCell align="center">Type</TableCell>
                <TableCell align="center">Remark</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.length > 0
                ? data?.map((d) => (
                  <TableRow key={d._id}>
                    <TableCell align="left">
                      <div className="tooltip1" style={{ cursor: 'pointer' }}>{d?.dealerCode}
                        <span className="tooltiptext1">{d?.dealerName}</span>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={d?.completed ? "Completed" : "Pending"}
                        color={d?.completed ? "success" : "warning"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <div className="tooltip" style={{ cursor: "pointer" }} onClick={() => {
                        setOpenLoc(true);
                        setLat(d?.startVisitLocation?.lat
                          ? d?.startVisitLocation?.lat
                          : "NA")
                        setLong(d?.startVisitLocation?.long
                          ? d?.startVisitLocation?.long
                          : "NA")
                      }}>
                        <RoomIcon color="primary" />
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <div className="tooltip" style={{ cursor: "pointer" }} onClick={() => {
                        setOpenLoc(true);
                        setLat(d?.endVisitLocation?.lat
                          ? d?.endVisitLocation?.lat
                          : "NA")
                        setLong(d?.endVisitLocation?.long
                          ? d?.endVisitLocation?.long
                          : "NA")
                      }}>
                        <RoomIcon color="secondary" />
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      {d?.startTime ? moment.utc(d?.startTime).format("DD-MM-YY hh:mm A") : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {d?.endTime ? moment.utc(d?.endTime).format("DD-MM-YY hh:mm A") : "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setOpen(true);
                        setDealers(d?.dealers);
                        setVisitDetails(d?.visitActions)
                      }}
                    >
                      <VisibilityIcon
                        style={{ cursor: 'pointer' }}
                        onClick={() => setOpen(true)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {/* location flag for 100m */}
                      {haversine(
                        {
                          lat: d?.startVisitLocation?.lat
                            ? d?.startVisitLocation?.lat
                            : 0,
                          lng: d?.startVisitLocation?.long
                            ? d?.startVisitLocation?.long
                            : 0,
                        },
                        {
                          lat: d?.endVisitLocation?.lat
                            ? d?.endVisitLocation?.lat
                            : 0,
                          lng: d?.endVisitLocation?.long
                            ? d?.endVisitLocation?.long
                            : 0,
                        }
                      ).toFixed(2) < 100
                        ? (<FlagIcon sx={{ color: "success.main" }} />)
                        : (<FlagIcon sx={{ color: "error.main" }} />)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        variant="outlined"
                        label={d?.unplanned ? "Unplanned" : "Planned"}
                        color={d?.unplanned ? "error" : "success"}
                      />
                    </TableCell>
                    <TableCell align="center" >
                      <Button variant="contained"
                        disabled={
                          d?.visitRemark.map((v, i) => {
                            if (v?.role == user?.role) return true
                            return false
                          }).includes(true)
                        }
                        onClick={() => {
                          setAddRemark(true);
                          setVisitId(d?._id)
                        }}>Add remark</Button>
                    </TableCell>
                  </TableRow>
                ))
                :
                <TableRow>
                  <TableCell colSpan={10} sx={{ border: 0 }}>
                    <EmptyState
                      title="No visits available"
                      description="No visits available for this SO in the selected date range."
                    />
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )
      }

      <Modal open={open} onClose={handleClose}>
        <Box sx={{ ...modalCardSx, width: { xs: '92%', sm: '70%', md: 560 }, maxHeight: '90vh', overflowY: 'auto' }}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align='left'>Type</TableCell>
                <TableCell>Action Taken</TableCell>
              </TableRow>
            </TableHead>

            {visitDetails?.length > 0 ? (
              visitDetails.map((row) => (
                <TableBody>
                  <TableRow key={row._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell align="left" scope="row" sx={{ borderBottom: 1, borderColor: 'divider' }}>{row?.type}</TableCell>
                    <TableCell align="center" scope="row" sx={{
                      borderBottom: 1, borderColor: 'divider', display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}>{row?.type === "image-capture" ?
                      <Box sx={{
                        backgroundImage: `url(${row?.imageCapturedUrl})`,
                        backgroundSize: 'cover',
                        width: '250px',
                        height: '250px',
                        position: "relative",
                      }}>
                        <Typography sx={{ color: "common.white", position: "absolute", bottom: 0, fontWeight: "bolder", textAlign: "center", width: '100%' }}>
                          {row?.locationString}
                        </Typography>
                      </Box> : row?.actionTaken}</TableCell>
                  </TableRow>
                </TableBody>))
            ) :
              <Box sx={{ textAlign: 'left', pt: 2 }}>
                <Typography variant="h4">No Visit Action Available.</Typography>
              </Box>
            }

          </Table>
          <Box sx={{ m: 3, display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setOpen(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal for location */}
      <Modal open={openLoc} onClose={handleClose}>
        <Box sx={messageStyleLoc}>
          <iframe src={`https://maps.google.com/maps?q=${lat},${long}&hl=es;&output=embed`} height="500px" width="100%"></iframe>
        </Box>
      </Modal>

      <Modal open={addRemark}
        onClose={() => {
          setAddRemark(false);
          setVisitId("")
        }}>
        <Box sx={{ ...modalCardSx, width: { xs: '90%', sm: '35%' }, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Visit Remark</Typography>
          <Box sx={{ display: 'flex', flexDirection: "column", p: 2 }}>
            <TextField
              type='text'
              value={visitRemark}
              onChange={(e) => setVisitRemark(e.target.value)}
              name="visitRemark"
              label="Add Visit Remark"
              variant="outlined"
            />

            <Button variant="contained" sx={{ mt: '1.5rem' }} onClick={submitRemark}>
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default VisitsList;
